import { Button } from '@/components/ui/shadcn/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/shadcn/card';
import { Progress } from '@/components/ui/shadcn/progress';
import { CheckCircle2, Loader2, X, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';

interface ImportProgressBarProps {
    importJobId: string;
    type: 'companies_csv' | 'company_registry_mapping';
    onClose: () => void;
    index?: number; // For stacking multiple progress bars
}

interface ImportProgress {
    id: string;
    type: string;
    status: 'pending' | 'running' | 'completed' | 'failed';
    progress: number;
    message: string;
    error?: string;
    metadata?: Record<string, number | string | undefined>;
    started_at?: string;
    completed_at?: string;
}

export default function ImportProgressBar({ importJobId, type, onClose, index = 0 }: ImportProgressBarProps) {
    const [progress, setProgress] = useState<ImportProgress | null>(null);
    const [isPolling, setIsPolling] = useState(true);

    useEffect(() => {
        if (!isPolling || !importJobId) return;

        const pollProgress = async () => {
            try {
                const response = await fetch(`/settings/system/import-progress/${importJobId}`, {
                    headers: {
                        Accept: 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                    credentials: 'same-origin',
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch progress');
                }

                const data: ImportProgress = await response.json();
                setProgress(data);

                // Stop polling if completed or failed
                if (data.status === 'completed' || data.status === 'failed') {
                    setIsPolling(false);
                    // Auto-close after 5 seconds if completed
                    if (data.status === 'completed') {
                        setTimeout(() => {
                            onClose();
                        }, 5000);
                    }
                }
            } catch (error) {
                console.error('Error polling import progress:', error);
            }
        };

        // Poll immediately, then every 2 seconds
        pollProgress();
        const interval = setInterval(pollProgress, 2000);

        return () => clearInterval(interval);
    }, [importJobId, isPolling, onClose]);

    const importTitle = type === 'company_registry_mapping' ? 'Registrų centras mapping' : 'Import Companies from CSV';

    if (!progress) {
        return (
            <Card className="fixed right-4 bottom-4 z-50 w-96 shadow-lg">
                <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                        <CardTitle className="text-sm">{importTitle}</CardTitle>
                        <Button variant="ghost" size="sm" onClick={onClose}>
                            <X className="h-4 w-4" />
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <p className="text-muted-foreground text-sm">Loading...</p>
                    </div>
                </CardContent>
            </Card>
        );
    }

    const getStatusIcon = () => {
        switch (progress.status) {
            case 'completed':
                return <CheckCircle2 className="text-success-subtle-foreground h-4 w-4" />;
            case 'failed':
                return <XCircle className="text-destructive h-4 w-4" />;
            case 'running':
                return <Loader2 className="text-info-subtle-foreground h-4 w-4 animate-spin" />;
            default:
                return <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />;
        }
    };

    // Calculate bottom offset for stacking (each card is ~140px tall + 16px gap)
    const bottomOffset = 16 + index * 156;

    return (
        <Card className="fixed right-4 z-50 w-96 border shadow-lg" style={{ bottom: `${bottomOffset}px` }}>
            <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        {getStatusIcon()}
                        <CardTitle className="text-sm">{importTitle}</CardTitle>
                    </div>
                    <Button variant="ghost" size="sm" onClick={onClose}>
                        <X className="h-4 w-4" />
                    </Button>
                </div>
            </CardHeader>
            <CardContent className="space-y-3">
                <div className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">{progress.message}</span>
                        <span className="font-medium">{progress.progress}%</span>
                    </div>
                    <Progress value={progress.progress} className="h-2" />
                </div>

                {progress.status === 'running' && (
                    <p className="text-muted-foreground text-xs">You can continue working while this runs in the background.</p>
                )}

                {progress.status === 'completed' && (
                    <div className="space-y-1">
                        <p className="text-success-subtle-foreground text-xs font-medium">Import completed successfully!</p>
                        {progress.metadata && (
                            <div className="text-muted-foreground text-xs">
                                {type === 'company_registry_mapping' && (
                                    <>
                                        {typeof progress.metadata.examined === 'number' && <p>Examined: {progress.metadata.examined}</p>}
                                        {typeof progress.metadata.updated === 'number' && <p>Updated: {progress.metadata.updated}</p>}
                                        {typeof progress.metadata.skipped_no_match === 'number' && (
                                            <p>No match: {progress.metadata.skipped_no_match}</p>
                                        )}
                                        {typeof progress.metadata.skipped_ambiguous === 'number' && (
                                            <p>Ambiguous: {progress.metadata.skipped_ambiguous}</p>
                                        )}
                                        {typeof progress.metadata.skipped_duplicate_code === 'number' && (
                                            <p>Duplicate code: {progress.metadata.skipped_duplicate_code}</p>
                                        )}
                                    </>
                                )}
                                {type !== 'company_registry_mapping' && progress.metadata.companies_imported && (
                                    <p>Companies imported: {progress.metadata.companies_imported}</p>
                                )}
                                {type !== 'company_registry_mapping' && progress.metadata.inserted && <p>Inserted: {progress.metadata.inserted}</p>}
                                {type !== 'company_registry_mapping' && progress.metadata.updated && <p>Updated: {progress.metadata.updated}</p>}
                                {type !== 'company_registry_mapping' && progress.metadata.skipped && <p>Skipped: {progress.metadata.skipped}</p>}
                                {type !== 'company_registry_mapping' && progress.metadata.addresses_updated && (
                                    <p>Addresses updated: {progress.metadata.addresses_updated}</p>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {progress.status === 'failed' && (
                    <div className="space-y-1">
                        <p className="text-destructive text-xs font-medium">Import failed</p>
                        {progress.error && <p className="text-muted-foreground text-xs">{progress.error}</p>}
                    </div>
                )}

                {progress.status === 'pending' && <p className="text-muted-foreground text-xs">Waiting to start...</p>}
            </CardContent>
        </Card>
    );
}
